import React, { ChangeEvent, FC } from "react";

import { Toggle, ToggleProps } from "@at/design-system-elements";
import { dataAttrTestId } from "@at/formatter-utils";

import { useQueryParameters } from "@at/hooks";

import * as Styled from "./lease-contract-switcher.styled";

import { LeaseContractType } from "../../../../../__generated__/graphql-types";
import {
  setBusinessTypeError,
  setLeasingContractType,
  setSelectedBusinessType,
} from "../../../../../contexts/leasing/actions/leasing.actions";
import {
  useLeasingDispatchContext,
  useLeasingStateContext,
} from "../../../../../contexts/leasing/context/leasing.context";
import { ELeasingBusinessType } from "../../../../../contexts/leasing/types/leasing.types";
import { useTracking } from "../../../../../utils/tracking/use-tracking";
import { getLeasingConfigEntity } from "../../../../../utils/tracking-util";

export const leaseContractSwitcherId = "lease-contract-switcher";
export const businessLeaseToggleText = "Business lease";
export const personalLeaseToggleText = "Personal lease";

export const LeaseContractSwitcher: FC = () => {
  const state = useLeasingStateContext();
  const dispatch = useLeasingDispatchContext();
  const { leaseContractType } = useLeasingStateContext();
  const { trackEvent } = useTracking();
  const leasingEntity = getLeasingConfigEntity(state);
  const { setQueryParamsAndHistory } = useQueryParameters();

  const handleLeaseContractSwitcher = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedLeaseContractType = event.currentTarget.value;

    if (selectedLeaseContractType === LeaseContractType.personal) {
      setQueryParamsAndHistory({ businessType: "" }, true);
      dispatch(setSelectedBusinessType(ELeasingBusinessType.DEFAULT));
      dispatch(setBusinessTypeError(false));
    }

    // If this change event value is not in the enum, then we don't want to do anything
    // If it is we're safe to cast the type to LeaseContractType and dispatch it
    if (
      !Object.values(LeaseContractType).includes(
        selectedLeaseContractType as LeaseContractType,
      )
    ) {
      return;
    }
    dispatch(
      setLeasingContractType(selectedLeaseContractType as LeaseContractType),
    );
    setQueryParamsAndHistory(
      {
        leaseContractType: selectedLeaseContractType.toLowerCase(),
      },
      true,
    );

    trackEvent({
      trackingLabel: `leasing-contract-type-selection`,
      replaceEntities: [
        {
          ...leasingEntity,
          lease_type:
            selectedLeaseContractType === LeaseContractType.business
              ? "BUSINESS"
              : "PERSONAL",
        },
      ],
    });
  };

  const toggleProps: ToggleProps = {
    name: leaseContractSwitcherId,
    id: leaseContractSwitcherId,
    options: [
      {
        label: personalLeaseToggleText,
        value: LeaseContractType.personal,
        selected: leaseContractType === LeaseContractType.personal,
      },
      {
        label: businessLeaseToggleText,
        value: LeaseContractType.business,
        selected: leaseContractType === LeaseContractType.business,
      },
    ],
    onInteraction: handleLeaseContractSwitcher,
    attributes: {
      [dataAttrTestId]: leaseContractSwitcherId,
    },
  };

  return (
    <Styled.ContractSwitchWrapper>
      <Toggle {...toggleProps} />
    </Styled.ContractSwitchWrapper>
  );
};
